import type { VideoSection as VideoSectionType } from '@shared/lib/page-sections/types';

const ASPECT: Record<NonNullable<VideoSectionType['aspect']>, string> = {
  '16/9': 'aspect-video',
  '4/3': 'aspect-[4/3]',
  '1/1': 'aspect-square',
};

function youtubeEmbedUrl(url: string): string | null {
  // Accept watch?v=, youtu.be/, /embed/ formats.
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function vimeoEmbedUrl(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : null;
}

export default function VideoSection({ section }: { section: VideoSectionType }) {
  if (!section.url) return null;
  const aspectClass = ASPECT[section.aspect ?? '16/9'];
  const embed = youtubeEmbedUrl(section.url) || vimeoEmbedUrl(section.url);

  return (
    <section className="w-full px-4 py-10 md:py-14">
      <div className="mx-auto max-w-4xl">
        <div className={`relative w-full overflow-hidden rounded-lg ${aspectClass}`}>
          {embed ? (
            <iframe
              src={embed}
              title={section.caption || 'Embedded video'}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="h-full w-full border-0"
            />
          ) : (
            <video src={section.url} controls className="h-full w-full object-cover" />
          )}
        </div>
        {section.caption ? (
          <p className="mt-3 text-center text-sm text-slate-500">{section.caption}</p>
        ) : null}
      </div>
    </section>
  );
}
